/**
 * use-audit.ts — Nhật ký kiểm toán: thao tác (owner/admin) + đăng nhập.
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import { toAuditParams, type AuditFilters } from '@/pages/settings/audit-lib'

export interface AuditActor { id: string; fullName: string; email: string }

export interface ActivityItem {
  id: string
  createdAt: string
  actor: AuditActor | null
  actorLabel: string
  action: string
  actionLabel: string
  module: string
  moduleLabel: string
  entityType: string | null
  entityId: string | null
  targetName: string | null
  detailText: string
}

export interface LoginItem {
  id: string
  userId: string
  user: AuditActor | null
  success: boolean
  method: string
  typeLabel: string
  ip: string | null
  ipChanged: boolean
  deviceType: string | null
  deviceLabel: string | null
  browser: string | null
  os: string | null
  location: string | null
  failureReason: string | null
  createdAt: string
}

export interface Paged<T> { items: T[]; total: number; page: number; limit: number }

export function useAuditModules(enabled = true) {
  return useQuery({
    queryKey: ['audit', 'modules'],
    enabled,
    staleTime: Infinity,
    queryFn: async () => (await api.get<{ modules: { id: string; label: string }[] }>('/audit/modules')).data.modules,
  })
}

export function useActivityLogs(f: AuditFilters, enabled = true) {
  return useQuery({
    queryKey: ['audit', 'activity', f],
    enabled,
    placeholderData: keepPreviousData,
    queryFn: async () => (await api.get<Paged<ActivityItem>>('/audit/activity', { params: toAuditParams(f) })).data,
  })
}

export function useLoginLogs(f: AuditFilters, enabled = true) {
  return useQuery({
    queryKey: ['audit', 'logins', f],
    enabled,
    placeholderData: keepPreviousData,
    queryFn: async () => (await api.get<Paged<LoginItem>>('/audit/logins', { params: toAuditParams(f) })).data,
  })
}

/** Tải CSV qua axios (kèm Bearer token) rồi lưu file — không đưa token lên URL. */
export type AuditExportKind = 'activity' | 'logins' | 'presence'

const EXPORT_FILENAMES: Record<AuditExportKind, string> = {
  activity: 'nhat-ky-thao-tac.csv',
  logins: 'nhat-ky-dang-nhap.csv',
  presence: 'nhat-ky-trang-thai-truc-tuyen.csv',
}

export async function downloadAuditCsv(kind: AuditExportKind, f: AuditFilters): Promise<void> {
  const params = toAuditParams({ ...f, page: undefined, limit: undefined })
  const res = await api.get<Blob>(`/audit/${kind}/export`, { params, responseType: 'blob' })
  const url = URL.createObjectURL(res.data)
  const a = document.createElement('a')
  a.href = url
  a.download = EXPORT_FILENAMES[kind]
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
